import * as mapConsts from '../components/map/const'
import { loadGoogleMapsLibraries } from './googleMapsLoader'

const NOMINATIM_REVERSE_GEOCODE_URL =
  'https://nominatim.openstreetmap.org/reverse'

const getAreaAndCityFromGoogle = (results = []) => {
  const addressComponents = results?.[0]?.address_components || []

  const area = addressComponents.find((c) =>
    c.types.includes('administrative_area_level_1')
  )
  const city = addressComponents.find((c) => c.types.includes('locality'))

  return {
    area: area?.long_name || null,
    city: city?.long_name || null,
  }
}

const getAreaAndCityFromNominatim = (address = {}) => {
  return {
    area: address.state || address.province || address.region || null,
    city:
      address.city ||
      address.town ||
      address.village ||
      address.hamlet ||
      address.municipality ||
      address.county ||
      null,
  }
}

const reverseGeocodeWithNominatim = async (location) => {
  const url = new URL(NOMINATIM_REVERSE_GEOCODE_URL)
  url.searchParams.set('format', 'jsonv2')
  url.searchParams.set('lat', String(location.lat))
  url.searchParams.set('lon', String(location.lng))
  url.searchParams.set('addressdetails', '1')

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`OSM reverse geocode failed with status: ${response.status}`)
  }

  const data = await response.json()
  const parsed = getAreaAndCityFromNominatim(data?.address)

  return {
    ...(parsed.area ? { area: parsed.area } : {}),
    ...(parsed.city ? { city: parsed.city } : {}),
  }
}

/**
 * A function to reverse geocode a latLng to get the area, city, state/province.
 * @param {Object} location - { lat: Number, lng: Number }
 * @returns {Object} - { area: String, city: String }
 */
export async function reverseGeocode(location) {
  const isValidLocation =
    location &&
    typeof location.lat === 'number' &&
    typeof location.lng === 'number' &&
    !Number.isNaN(location.lat) &&
    !Number.isNaN(location.lng)

  if (!isValidLocation) {
    console.error('reverseGeocode called with invalid location:', location)
    return null
  }

  try {
    const hasGoogleApiKey = Boolean(mapConsts.loaderOptions?.apiKey)

    if (hasGoogleApiKey) {
      await loadGoogleMapsLibraries(['geocoding'])

      const geoCoder = new window.google.maps.Geocoder()

      if (!geoCoder) throw new Error('Geocoder not found')

      const point = new window.google.maps.LatLng(location.lat, location.lng)

      const googleResult = await new Promise((resolve, reject) => {
        geoCoder.geocode({ location: point }, (results, status) => {
          if (status === 'OK') {
            const parsed = getAreaAndCityFromGoogle(results)
            resolve({
              ...(parsed.area ? { area: parsed.area } : {}),
              ...(parsed.city ? { city: parsed.city } : {}),
            })
            return
          }

          reject(new Error('Geocoder failed with status: ' + status))
        })
      })

      if (Object.keys(googleResult).length > 0) return googleResult
    }
  } catch (e) {
    console.warn('Google reverse geocoding failed, trying OSM fallback:', e)
  }

  try {
    const osmResult = await reverseGeocodeWithNominatim(location)
    if (Object.keys(osmResult).length > 0) return osmResult
  } catch (osmError) {
    console.error('OSM reverse geocoding failed:', osmError)
  }

  return null
}
